// AiMessageRow.jsx

import React, { useCallback } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatChannelRelativeTime } from 'channels/utils/channelTime';

// Constants
import { AI_TITLE } from 'spaces/constants/aiConstants';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './AiMessageRow.css';
import './AiSurfaceMessage.css';

// Components
import PlanesMark from 'components/logo/mark/PlanesMark';
import AiFormattedText from 'spaces/components/AiFormattedText';
import AiMessageActions from 'spaces/components/AiMessageActions';
import AiProvenancePills from 'spaces/components/AiProvenancePills';
import AiThreadChrome from 'spaces/components/AiThreadChrome';

const MemberProvenanceOpen = ({
  citations,
  onOpenCitation,
}) => {
  const { openNode } = useNodeOpenIntent();

  const handleOpenRecord = useCallback((citation) => {
    if (typeof onOpenCitation === 'function') {
      onOpenCitation(citation);
      return;
    }
    const nodeKey = String(citation?.nodeKey || citation?.sourceNodeKey || '').trim();
    if (!nodeKey) return;
    openNode({
      key: nodeKey,
      title: citation?.title || 'Source',
      type: citation?.type || '',
      subtitle: citation?.excerpt || '',
    });
  }, [onOpenCitation, openNode]);

  const handleOpenOrigin = useCallback((permalink) => {
    const href = String(permalink || '').trim();
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <AiProvenancePills
      citations={citations}
      onOpenRecord={handleOpenRecord}
      onOpenOrigin={handleOpenOrigin}
    />
  );
};

const AiMessageRow = React.memo(({
  message,
  promptText = '',
  disambiguationEnabled = true,
  onSelectDisambiguationOption = null,
  canReplyInThread = false,
  hasUnreadReplies = false,
  onOpenThread,
  onReplyInThread,
  threadSummariesByRootKey = {},
  onOpenCitation = null,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const createdLabel = formatChannelRelativeTime(message?.dateCreated);
  const citations = Array.isArray(message?.citations) ? message.citations : [];

  // ----- RENDER ----- //

  return (
    <div className="ai-message-row" id={`message-${message?.key || ''}`}>
      <div
        className="ai-message-row__avatar"
        aria-hidden
      >
        <PlanesMark size={32} variant="brand" />
      </div>

      <div className="ai-message-row__body">
        <div className="ai-message-row__header">
          <span
            className="ai-message-row__name"
            style={{
              color: theme.foreground,
            }}
          >
            {AI_TITLE}
          </span>
          {createdLabel ? (
            <span
              className="ai-message-row__time"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              {createdLabel}
            </span>
          ) : null}
        </div>

        <div
          className="ai-message-row__text"
          style={{
            color: theme.foreground,
          }}
        >
          <AiFormattedText text={message?.text || ''} keyPrefix="message" />
          <AiMessageActions
            message={message}
            disambiguationEnabled={disambiguationEnabled}
            onSelectDisambiguationOption={onSelectDisambiguationOption}
          />
          {citations.length > 0 ? (
            <MemberProvenanceOpen
              citations={citations}
              onOpenCitation={onOpenCitation}
            />
          ) : null}
        </div>

        <AiThreadChrome
          message={message}
          promptText={promptText}
          canReplyInThread={canReplyInThread}
          hasUnreadReplies={hasUnreadReplies}
          threadSummariesByRootKey={threadSummariesByRootKey}
          onOpenThread={onOpenThread}
          onReplyInThread={onReplyInThread}
        />
      </div>
    </div>
  );
});

AiMessageRow.displayName = 'AiMessageRow';

export default AiMessageRow;
